/**
 * Sentry Custom Breadcrumbs Module
 *
 * This module provides custom breadcrumb logging functions for Sentry to track
 * user flows and application events. Breadcrumbs are automatically attached to
 * error events to provide context about what the user was doing before the error occurred.
 *
 * Privacy:
 * - Does NOT log sensitive user data (passwords, answers, PII)
 * - Only logs metadata like IDs, actions, status codes
 * - Follows GDPR compliance requirements
 *
 * Features:
 * - Onboarding question tracking (without answer content)
 * - Recommendation interaction tracking
 * - Critical API call tracking with timing
 * - Navigation tracking between screens
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5
 */

import * as Sentry from '@sentry/react-native';
import logger from './logger';

/**
 * Maximum number of breadcrumbs to retain (Sentry default: 100)
 * Older breadcrumbs are automatically removed by Sentry
 */
const MAX_BREADCRUMBS = 100;

/**
 * Logs completion of an onboarding question.
 *
 * This breadcrumb tracks user progress through the onboarding flow without
 * capturing sensitive answer content.
 *
 * Category: onboarding
 * Level: info
 *
 * @param {string} questionId - Unique identifier for the question (e.g., 'budget', 'body_type')
 * @param {any} answer - User's answer (NOT logged to protect privacy)
 *
 * @example
 * logOnboardingQuestionCompleted('budget', '20000-30000');
 * // Breadcrumb: { category: 'onboarding', questionId: 'budget', hasAnswer: true }
 *
 * @requirement 9.1 (Custom breadcrumbs for user flows)
 */
export function logOnboardingQuestionCompleted(questionId, answer) {
  try {
    if (!questionId) {
      logger.warn('[Breadcrumb] logOnboardingQuestionCompleted: questionId is required');
      return;
    }

    // Log breadcrumb WITHOUT the answer content for privacy
    Sentry.addBreadcrumb({
      category: 'onboarding',
      message: `Question completed: ${questionId}`,
      level: 'info',
      data: {
        questionId,
        hasAnswer: answer !== undefined && answer !== null && answer !== '',
        timestamp: new Date().toISOString(),
      },
    });

    logger.log(`[Breadcrumb] Onboarding question completed: ${questionId}`);
  } catch (error) {
    // Fail silently - breadcrumb logging should never break the app
    logger.error('[Breadcrumb] Error logging onboarding question:', error);
  }
}

/**
 * Logs user interaction with a recommendation.
 *
 * This breadcrumb tracks how users engage with car recommendations
 * (like, dislike, view details, etc.).
 *
 * Category: user_interaction
 * Level: info
 *
 * @param {string} recommendationId - Unique identifier for the recommendation
 * @param {string} action - Type of interaction (e.g., 'like', 'dislike', 'view', 'share')
 *
 * @example
 * logRecommendationInteraction('rec-12345', 'like');
 * // Breadcrumb: { category: 'user_interaction', recommendationId: 'rec-12345', action: 'like' }
 *
 * @requirement 9.2 (Custom breadcrumbs for recommendation interactions)
 */
export function logRecommendationInteraction(recommendationId, action) {
  try {
    if (!recommendationId || !action) {
      logger.warn(
        '[Breadcrumb] logRecommendationInteraction: recommendationId and action are required'
      );
      return;
    }

    Sentry.addBreadcrumb({
      category: 'user_interaction',
      message: `Recommendation ${action}: ${recommendationId}`,
      level: 'info',
      data: {
        recommendationId,
        action,
        timestamp: new Date().toISOString(),
      },
    });

    logger.log(`[Breadcrumb] Recommendation interaction: ${action} on ${recommendationId}`);
  } catch (error) {
    logger.error('[Breadcrumb] Error logging recommendation interaction:', error);
  }
}

/**
 * Logs a critical API call with status and duration metrics.
 *
 * This breadcrumb tracks API calls to external services (Supabase, Langflow, etc.)
 * to help diagnose issues with backend connectivity and performance.
 *
 * Category: api
 * Level: info (success) or error (failure)
 *
 * @param {string} apiName - Name of the API being called (e.g., 'supabase.fetch_recommendations', 'langflow.chat')
 * @param {string} status - Status of the call ('success', 'error', 'timeout', HTTP status code)
 * @param {number} duration - Duration of the call in milliseconds
 *
 * @example
 * logCriticalApiCall('supabase.fetch_recommendations', 'success', 234);
 * // Breadcrumb: { category: 'api', apiName: 'supabase.fetch_recommendations', status: 'success', duration: 234 }
 *
 * @example
 * logCriticalApiCall('langflow.chat', 'error', 5000);
 * // Breadcrumb: { category: 'api', apiName: 'langflow.chat', status: 'error', duration: 5000, level: 'error' }
 *
 * @requirement 9.3 (Custom breadcrumbs for API calls)
 */
export function logCriticalApiCall(apiName, status, duration) {
  try {
    if (!apiName || !status) {
      logger.warn('[Breadcrumb] logCriticalApiCall: apiName and status are required');
      return;
    }

    // Determine breadcrumb level based on status
    // Error states: 'error', 'timeout', HTTP 4xx/5xx codes
    const isError =
      status === 'error' ||
      status === 'timeout' ||
      (typeof status === 'number' && status >= 400) ||
      (typeof status === 'string' && /^[45]\d{2}$/.test(status));

    const level = isError ? 'error' : 'info';

    Sentry.addBreadcrumb({
      category: 'api',
      message: `API call: ${apiName} (${status})`,
      level,
      data: {
        apiName,
        status,
        duration: duration ? Math.round(duration) : undefined,
        timestamp: new Date().toISOString(),
      },
    });

    logger.log(`[Breadcrumb] API call: ${apiName} - ${status} (${duration}ms) [level: ${level}]`);
  } catch (error) {
    logger.error('[Breadcrumb] Error logging API call:', error);
  }
}

/**
 * Logs navigation between screens in the app.
 *
 * This breadcrumb tracks the user's navigation path, which is crucial for
 * understanding the context of errors (e.g., which screen was the user on?).
 *
 * Category: navigation
 * Level: info
 *
 * @param {string} from - Screen name the user is navigating from (or null for initial navigation)
 * @param {string} to - Screen name the user is navigating to
 *
 * @example
 * logNavigation('Dashboard', 'ProductDetail');
 * // Breadcrumb: { category: 'navigation', from: 'Dashboard', to: 'ProductDetail' }
 *
 * @example
 * logNavigation(null, 'Onboarding');
 * // Breadcrumb: { category: 'navigation', from: null, to: 'Onboarding' }
 *
 * @requirement 9.4 (Custom breadcrumbs for navigation)
 */
export function logNavigation(from, to) {
  try {
    if (!to) {
      logger.warn('[Breadcrumb] logNavigation: "to" parameter is required');
      return;
    }

    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigation: ${from || 'App Start'} → ${to}`,
      level: 'info',
      data: {
        from: from || null,
        to,
        timestamp: new Date().toISOString(),
      },
    });

    logger.log(`[Breadcrumb] Navigation: ${from || 'App Start'} → ${to}`);
  } catch (error) {
    logger.error('[Breadcrumb] Error logging navigation:', error);
  }
}

/**
 * Logs car selection interactions.
 *
 * Category: car_selection
 * Level: info
 *
 * @param {string} action - Type of action: 'toggle', 'submit'
 * @param {object} data - Action-specific data (model_id, selected_count, etc.)
 */
export function logCarSelection(action, data = {}) {
  try {
    Sentry.addBreadcrumb({
      category: 'car_selection',
      message: `Car selection: ${action}`,
      level: 'info',
      data: { action, ...data, timestamp: new Date().toISOString() },
    });
    logger.log(`[Breadcrumb] Car selection: ${action}`);
  } catch (error) {
    logger.error('[Breadcrumb] Error logging car selection:', error);
  }
}

/**
 * Logs feed interaction events.
 *
 * Category: feed_interaction
 * Level: info
 *
 * @param {string} action - Type of action: 'card_tap', 'save', 'unsave', 'filter', 'scroll'
 * @param {object} data - Action-specific data (feed_entry_id, model_id, etc.)
 */
export function logFeedInteraction(action, data = {}) {
  try {
    Sentry.addBreadcrumb({
      category: 'feed_interaction',
      message: `Feed interaction: ${action}`,
      level: 'info',
      data: { action, ...data, timestamp: new Date().toISOString() },
    });
    logger.log(`[Breadcrumb] Feed interaction: ${action}`);
  } catch (error) {
    logger.error('[Breadcrumb] Error logging feed interaction:', error);
  }
}

/**
 * Logs listing detail page actions.
 *
 * Category: listing_detail
 * Level: info
 *
 * @param {string} action - Type of action: 'dealer_click', 'share', 'dismiss', 'image_swipe'
 * @param {object} data - Action-specific data (feed_entry_id, vdp_url, etc.)
 */
export function logListingDetailAction(action, data = {}) {
  try {
    Sentry.addBreadcrumb({
      category: 'listing_detail',
      message: `Listing detail: ${action}`,
      level: 'info',
      data: { action, ...data, timestamp: new Date().toISOString() },
    });
    logger.log(`[Breadcrumb] Listing detail: ${action}`);
  } catch (error) {
    logger.error('[Breadcrumb] Error logging listing detail action:', error);
  }
}

/**
 * Export all breadcrumb functions as default for easy testing
 */
export default {
  logOnboardingQuestionCompleted,
  logRecommendationInteraction,
  logCriticalApiCall,
  logNavigation,
  logCarSelection,
  logFeedInteraction,
  logListingDetailAction,
  MAX_BREADCRUMBS,
};
